import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  MessageSquare,
  MousePointer2,
  PanelRight,
  Sparkles,
  X,
} from 'lucide-react';
import { useComments } from './CommentContext';

// Mounted once per version subtree (alongside <CommentOverlay />). Shows a
// 3-step carousel + name capture when a reviewer first lands without a name
// in localStorage. Dismissal is persistent (mock-ui-intro-seen) so the intro
// never re-shows for the same browser. Force it back with ?intro=1.

const STORAGE_KEY = 'mock-ui-intro-seen';
const REPLAY_EVENT = 'mock-ui-show-intro';

// Re-open the intro carousel from anywhere (e.g. the TopBar help button).
// The mounted <CommentIntro /> listens for this event and resets its state.
export function replayCommentIntro() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(REPLAY_EVENT));
}

type Slide = {
  title: string;
  body: ReactNode;
  demo: ReactNode;
};

const SLIDES: Slide[] = [
  {
    title: 'Welcome — this is the mock',
    body: (
      <>
        Your feedback shapes it. The fastest way to leave one: hold{' '}
        <Kbd>Alt</Kbd> / <Kbd>Option</Kbd> and click any UI surface.
      </>
    ),
    demo: <AltClickDemo />,
  },
  {
    title: 'Browsing with Comment Mode + the sidebar',
    body: (
      <>
        For a sustained review session, press <Kbd>C</Kbd> — or click the
        chat-bubble icon{' '}
        <InlineIcon>
          <MessageSquare size={11} strokeWidth={2.25} aria-hidden="true" />
        </InlineIcon>{' '}
        in the top-right of the top bar — to enter Comment Mode. Anything
        reviewable lights up; click to drop a pin. The panel-toggle icon{' '}
        <InlineIcon>
          <PanelRight size={11} strokeWidth={2.25} aria-hidden="true" />
        </InlineIcon>{' '}
        right next to it opens the sidebar, where every comment is grouped
        by version with reply / resolve actions.
      </>
    ),
    demo: <CommentModeDemo />,
  },
  {
    title: 'Versions & changelog',
    body: (
      <>
        The top-bar <strong>version picker</strong> switches between cuts —
        the chip tells you which is live. The{' '}
        <strong>Changelog</strong> rail entry is the meeting-driven timeline
        of what shipped and what's planned. Action chips carry state:{' '}
        <span style={{ whiteSpace: 'nowrap' }}>✓ shipped</span> ·{' '}
        <span style={{ whiteSpace: 'nowrap' }}>⏱ in-flight</span> ·{' '}
        <span style={{ whiteSpace: 'nowrap' }}>○ planned</span> ·{' '}
        <span style={{ whiteSpace: 'nowrap' }}>? blocked</span>.
      </>
    ),
    demo: <VersionChangelogDemo />,
  },
];

export function CommentIntro() {
  const { username, setUsername } = useComments();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // Force-show via ?intro=1 (for QA / first-time UX testing without clearing
  // localStorage). Strip the param after we've consumed it so refreshes don't
  // re-trigger after the user dismisses.
  const forced = params.get('intro') === '1';

  const [introSeen, setIntroSeen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  });
  const [step, setStep] = useState(0);
  const [nameInput, setNameInput] = useState('');

  // Suppress on the auth/login screen — that scene runs without chrome and
  // the intro on top of a half-rendered page is jarring.
  const onLoginRoute = location.pathname === '/login';

  const shouldShow = !onLoginRoute && (forced || (!username && !introSeen));

  // Lock body scroll while open so the page beneath can't be navigated.
  useEffect(() => {
    if (!shouldShow) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shouldShow]);

  // Esc dismisses (same as Skip).
  useEffect(() => {
    if (!shouldShow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  // Re-open trigger — fired by the TopBar help button. Resets carousel state
  // to slide 0 so the replay always starts from the welcome.
  useEffect(() => {
    const onReplay = () => {
      setIntroSeen(false);
      setStep(0);
      setNameInput('');
    };
    window.addEventListener(REPLAY_EVENT, onReplay);
    return () => window.removeEventListener(REPLAY_EVENT, onReplay);
  }, []);

  if (!shouldShow) return null;

  function dismiss() {
    window.localStorage.setItem(STORAGE_KEY, '1');
    setIntroSeen(true);
    if (forced) {
      const next = new URLSearchParams(params);
      next.delete('intro');
      setParams(next, { replace: true });
    }
  }

  function finish() {
    const trimmed = nameInput.trim().slice(0, 60);
    if (trimmed) setUsername(trimmed);
    dismiss();
  }

  const totalSteps = SLIDES.length + 1; // 3 carousel + 1 name
  const isNameStep = step === SLIDES.length;
  const isLastCarousel = step === SLIDES.length - 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        className="glass-deep"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 520,
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,0,0,0.28)',
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Skip intro"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-tertiary)',
            padding: 6,
            display: 'inline-flex',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>

        <div
          style={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'linear-gradient(180deg, var(--surface-2) 0%, var(--surface-1) 100%)',
            borderBottom: '1px solid var(--hairline)',
            overflow: 'hidden',
          }}
        >
          {isNameStep ? <NameStepIllustration /> : SLIDES[step].demo}
        </div>

        <div style={{ padding: '20px 24px 16px' }}>
          {!isNameStep && (
            <>
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--ink-primary)',
                  margin: 0,
                  marginBottom: 8,
                }}
              >
                {SLIDES[step].title}
              </h2>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'var(--ink-secondary)',
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {SLIDES[step].body}
              </p>
            </>
          )}
          {isNameStep && (
            <>
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--ink-primary)',
                  margin: 0,
                  marginBottom: 8,
                }}
              >
                One last thing — what should we call you?
              </h2>
              <p
                style={{
                  fontSize: 12.5,
                  color: 'var(--ink-secondary)',
                  lineHeight: 1.55,
                  margin: 0,
                  marginBottom: 12,
                }}
              >
                Your name shows on the comments you leave so we can follow up.
              </p>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Your name"
                maxLength={60}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nameInput.trim()) finish();
                }}
                style={inputStyle}
              />
            </>
          )}
        </div>

        <div
          style={{
            padding: '12px 24px 16px',
            borderTop: '1px solid var(--hairline)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  width: i === step ? 18 : 6,
                  height: 6,
                  borderRadius: 3,
                  background:
                    i === step
                      ? 'var(--accent)'
                      : 'var(--hairline-strong)',
                  transition: 'all 150ms ease',
                }}
              />
            ))}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                style={ghostButtonStyle}
              >
                <ArrowLeft size={12} aria-hidden="true" />
                <span style={{ marginLeft: 4 }}>Back</span>
              </button>
            )}
            {!isNameStep && (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                style={primaryButtonStyle(false)}
              >
                <span>{isLastCarousel ? 'Almost done' : 'Next'}</span>
                <ArrowRight size={12} aria-hidden="true" style={{ marginLeft: 4 }} />
              </button>
            )}
            {isNameStep && (
              <button
                type="button"
                onClick={finish}
                style={primaryButtonStyle(false)}
              >
                Start reviewing
                <Sparkles size={12} aria-hidden="true" style={{ marginLeft: 4 }} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline demos
// ---------------------------------------------------------------------------

function AltClickDemo() {
  return (
    <div style={{ position: 'relative', width: 320, height: 160 }}>
      <style>{`
        @keyframes intro-alt-pulse {
          0%, 25%, 100% { background: var(--surface-1); color: var(--ink-primary); border-color: var(--hairline-strong); }
          35%, 60% { background: var(--accent); color: #fff; border-color: var(--accent); }
        }
        @keyframes intro-cursor-move {
          0%, 20% { transform: translate(20px, 18px); }
          55%, 100% { transform: translate(184px, 86px); }
        }
        @keyframes intro-anchor-glow {
          0%, 55% { box-shadow: 0 0 0 0 rgba(225,138,47,0); border-color: var(--hairline-strong); }
          65%, 100% { box-shadow: 0 0 0 3px rgba(225,138,47,0.18); border-color: var(--accent); }
        }
        @keyframes intro-pin-pop {
          0%, 60% { opacity: 0; transform: scale(0.4); }
          72% { opacity: 1; transform: scale(1.18); }
          85%, 100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div
        style={{
          position: 'absolute',
          left: 60,
          top: 28,
          width: 200,
          height: 104,
          background: 'var(--surface-1)',
          border: '1px solid var(--hairline-strong)',
          borderRadius: 6,
          animation: 'intro-anchor-glow 3.2s ease-in-out infinite',
        }}
      >
        <div
          style={{
            padding: '8px 12px',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            color: 'var(--ink-tertiary)',
          }}
        >
          Project Overview
        </div>
        <div
          style={{
            margin: '0 12px',
            height: 4,
            background: 'var(--hairline)',
            borderRadius: 2,
          }}
        />
        <div
          style={{
            margin: '8px 12px 0',
            height: 4,
            width: '60%',
            background: 'var(--hairline)',
            borderRadius: 2,
          }}
        />
        <div
          style={{
            margin: '8px 12px 0',
            height: 4,
            width: '40%',
            background: 'var(--hairline)',
            borderRadius: 2,
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 14,
          bottom: 16,
          fontSize: 10,
          fontWeight: 600,
          padding: '4px 10px',
          borderRadius: 5,
          border: '1px solid var(--hairline-strong)',
          fontFamily: 'inherit',
          animation: 'intro-alt-pulse 3.2s ease-in-out infinite',
        }}
      >
        ⌥ Alt
      </div>

      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          animation: 'intro-cursor-move 3.2s ease-in-out infinite',
          color: 'var(--ink-primary)',
        }}
      >
        <MousePointer2
          size={18}
          strokeWidth={2.25}
          style={{
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 196,
          top: 96,
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--accent)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          animation: 'intro-pin-pop 3.2s ease-in-out infinite',
        }}
      >
        <MessageSquare size={12} strokeWidth={2.25} aria-hidden="true" />
      </div>
    </div>
  );
}

function CommentModeDemo() {
  return (
    <div style={{ position: 'relative', width: 320, height: 160 }}>
      <style>{`
        @keyframes intro-c-pulse {
          0%, 18%, 100% { background: var(--surface-1); color: var(--ink-primary); border-color: var(--hairline-strong); }
          26%, 45% { background: var(--accent); color: #fff; border-color: var(--accent); }
        }
        @keyframes intro-mode-tint {
          0%, 25% { opacity: 0; }
          40%, 100% { opacity: 0.5; }
        }
        @keyframes intro-mode-pin-pop {
          0%, 50% { opacity: 0; transform: scale(0.4); }
          62% { opacity: 1; transform: scale(1.15); }
          75%, 100% { opacity: 1; transform: scale(1); }
        }
        @keyframes intro-sidebar-slide {
          0%, 65% { transform: translateX(110%); }
          80%, 100% { transform: translateX(0); }
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 6,
          border: '2px solid var(--accent)',
          borderRadius: 6,
          pointerEvents: 'none',
          animation: 'intro-mode-tint 3.5s ease-in-out infinite',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: 14,
          top: 14,
          fontSize: 10,
          fontWeight: 600,
          padding: '4px 9px',
          borderRadius: 5,
          border: '1px solid var(--hairline-strong)',
          fontFamily: 'inherit',
          animation: 'intro-c-pulse 3.5s ease-in-out infinite',
        }}
      >
        C
      </div>

      <div
        style={{
          position: 'absolute',
          left: 60,
          top: 50,
          width: 130,
          height: 68,
          background: 'var(--surface-1)',
          border: '1px solid var(--hairline-strong)',
          borderRadius: 6,
        }}
      >
        <div
          style={{
            padding: 8,
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            color: 'var(--ink-tertiary)',
          }}
        >
          Chapter 11
        </div>
        <div
          style={{
            margin: '0 8px',
            height: 4,
            background: 'var(--hairline)',
            borderRadius: 2,
          }}
        />
        <div
          style={{
            margin: '6px 8px 0',
            height: 4,
            width: '70%',
            background: 'var(--hairline)',
            borderRadius: 2,
          }}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 168,
          top: 92,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'var(--accent)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          animation: 'intro-mode-pin-pop 3.5s ease-in-out infinite',
        }}
      >
        <MessageSquare size={11} strokeWidth={2.25} aria-hidden="true" />
      </div>

      <div
        style={{
          position: 'absolute',
          right: 6,
          top: 6,
          bottom: 6,
          width: 100,
          background: 'var(--surface-1)',
          border: '1px solid var(--hairline)',
          borderRadius: 6,
          padding: 8,
          animation: 'intro-sidebar-slide 3.5s ease-in-out infinite',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 9,
            fontWeight: 600,
            color: 'var(--ink-primary)',
            marginBottom: 6,
          }}
        >
          <MessageSquare
            size={9}
            aria-hidden="true"
            style={{ color: 'var(--accent)' }}
          />
          Comments
        </div>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              marginBottom: 4,
              padding: '4px 6px',
              border: '1px solid var(--hairline)',
              borderRadius: 4,
              background:
                i === 0 ? 'rgba(225,138,47,0.08)' : 'var(--surface-2)',
            }}
          >
            <div
              style={{
                height: 3,
                width: '70%',
                background: 'var(--hairline-strong)',
                borderRadius: 2,
                marginBottom: 3,
              }}
            />
            <div
              style={{
                height: 3,
                width: '40%',
                background: 'var(--hairline)',
                borderRadius: 2,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function VersionChangelogDemo() {
  return (
    <div style={{ position: 'relative', width: 360, height: 168 }}>
      <style>{`
        @keyframes intro-dropdown-open {
          0%, 35% { opacity: 0; transform: translateY(-4px) scaleY(0.92); }
          45%, 100% { opacity: 1; transform: translateY(0) scaleY(1); }
        }
        @keyframes intro-chevron-spin {
          0%, 30% { transform: rotate(0deg); }
          45%, 100% { transform: rotate(180deg); }
        }
        @keyframes intro-picker-glow {
          0%, 25% { box-shadow: 0 0 0 0 rgba(225,138,47,0); }
          40%, 100% { box-shadow: 0 0 0 3px rgba(225,138,47,0.22); }
        }
      `}</style>

      {/* Abstract app frame — top bar + left rail + content well, all
          mid-fidelity, so the dropdown reads as living in the top-right
          corner of the actual product. */}
      <div
        style={{
          position: 'absolute',
          inset: 6,
          background: 'var(--surface-1)',
          border: '1px solid var(--hairline-strong)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Top bar */}
        <div
          style={{
            height: 24,
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            gap: 8,
            position: 'relative',
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 5,
              background: 'var(--accent)',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              gap: 6,
              alignItems: 'center',
            }}
          >
            <div style={breadcrumbStripeStyle(36)} />
            <div style={breadcrumbStripeStyle(4)} />
            <div style={breadcrumbStripeStyle(48)} />
          </div>

          {/* Version picker chip — anchored to top-right; dropdown opens
              downward from this exact corner. */}
          <div
            style={{
              position: 'relative',
              zIndex: 2,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 7px',
                border: '1px solid var(--accent)',
                borderRadius: 4,
                background: 'var(--surface-1)',
                fontSize: 9.5,
                fontWeight: 500,
                color: 'var(--ink-primary)',
                animation: 'intro-picker-glow 3.6s ease-in-out infinite',
              }}
            >
              <span>v2.8.1</span>
              <ChevronDown
                size={10}
                aria-hidden="true"
                style={{
                  color: 'var(--ink-tertiary)',
                  animation: 'intro-chevron-spin 3.6s ease-in-out infinite',
                }}
              />
            </div>
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 4,
                width: 124,
                background: 'var(--surface-1)',
                border: '1px solid var(--hairline-strong)',
                borderRadius: 5,
                transformOrigin: 'top right',
                animation: 'intro-dropdown-open 3.6s ease-in-out infinite',
                boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 9.5,
                  color: 'var(--ink-primary)',
                }}
              >
                <span>v2.8.1</span>
                <span
                  style={{
                    fontSize: 8,
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: 'var(--accent)',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                >
                  Live
                </span>
              </div>
              <div
                style={{
                  padding: '4px 8px',
                  fontSize: 9.5,
                  color: 'var(--ink-tertiary)',
                  borderTop: '1px solid var(--hairline)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>v1.2.1</span>
                <span style={{ fontSize: 8 }}>Previous</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body: left rail + changelog card */}
        <div style={{ display: 'flex', height: 'calc(100% - 24px)' }}>
          <div
            style={{
              width: 26,
              borderRight: '1px solid var(--hairline)',
              background: 'var(--surface-2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '7px 0',
              gap: 5,
            }}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background:
                    i === 2
                      ? 'var(--accent)'
                      : 'var(--hairline-strong)',
                  opacity: i === 2 ? 1 : 0.55,
                }}
              />
            ))}
          </div>

          <div style={{ flex: 1, padding: '10px 12px' }}>
            <div
              style={{
                fontSize: 9,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                color: 'var(--ink-tertiary)',
                marginBottom: 6,
              }}
            >
              Changelog
            </div>
            <div
              style={{
                fontSize: 10,
                color: 'var(--ink-primary)',
                fontWeight: 500,
                marginBottom: 5,
                lineHeight: 1.4,
              }}
            >
              Overview reframe — sections 1-4
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <ActionChip state="shipped" label="A3" />
              <ActionChip state="planned" label="A12" />
              <ActionChip state="inflight" label="A28" />
              <ActionChip state="blocked" label="A19" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function breadcrumbStripeStyle(width: number): CSSProperties {
  return {
    width,
    height: 4,
    borderRadius: 2,
    background: 'var(--hairline)',
  };
}

function NameStepIllustration() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--accent)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(225,138,47,0.35)',
        }}
      >
        <Sparkles size={24} strokeWidth={2.25} aria-hidden="true" />
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: 'var(--ink-tertiary)',
          letterSpacing: '0.04em',
        }}
      >
        Ready when you are.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function InlineIcon({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: 4,
        background: 'var(--surface-1)',
        border: '1px solid var(--hairline-strong)',
        color: 'var(--ink-primary)',
        verticalAlign: '-3px',
      }}
    >
      {children}
    </span>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        margin: '0 1px',
        borderRadius: 4,
        border: '1px solid var(--hairline-strong)',
        background: 'var(--surface-1)',
        color: 'var(--ink-primary)',
        fontSize: 10.5,
        fontWeight: 600,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        lineHeight: 1.4,
      }}
    >
      {children}
    </span>
  );
}

function ActionChip({
  state,
  label,
}: {
  state: 'shipped' | 'inflight' | 'planned' | 'blocked';
  label: string;
}) {
  const config: Record<
    typeof state,
    { glyph: ReactNode; bg: string; fg: string }
  > = {
    shipped: {
      glyph: <Check size={8} strokeWidth={3} aria-hidden="true" />,
      bg: 'rgba(106, 153, 78, 0.16)',
      fg: '#4f7d3a',
    },
    inflight: {
      glyph: <span style={{ fontSize: 8, lineHeight: 1 }}>⏱</span>,
      bg: 'rgba(225, 138, 47, 0.16)',
      fg: 'var(--accent)',
    },
    planned: {
      glyph: <span style={{ fontSize: 8, lineHeight: 1 }}>○</span>,
      bg: 'var(--surface-2)',
      fg: 'var(--ink-tertiary)',
    },
    blocked: {
      glyph: <span style={{ fontSize: 8, lineHeight: 1 }}>?</span>,
      bg: 'rgba(177, 69, 69, 0.14)',
      fg: 'var(--state-bad, #b14545)',
    },
  };
  const c = config[state];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: '1px 5px',
        borderRadius: 3,
        fontSize: 9,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
      }}
    >
      {c.glyph}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--hairline-strong)',
  borderRadius: 6,
  background: 'var(--bg-elevated, var(--bg-base))',
  color: 'var(--ink-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const ghostButtonStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--hairline-strong)',
  borderRadius: 5,
  padding: '5px 11px',
  fontSize: 11.5,
  color: 'var(--ink-secondary)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

function primaryButtonStyle(disabled: boolean): CSSProperties {
  return {
    background: disabled ? 'var(--hairline-strong)' : 'var(--accent)',
    border: 'none',
    borderRadius: 5,
    padding: '5px 13px',
    fontSize: 11.5,
    color: disabled ? 'var(--ink-tertiary)' : '#fff',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  };
}
